import { DolfinParser } from './parsers/base'
import { IntToFloatParser } from './parsers/int-to-float'
import { CustomFieldParser } from './parsers/custom-field-parser'
import { LegacyDbVariableParser } from './parsers/legacy-db-variable-parser'
import { RecordParser } from './parsers/record-parser'
import { OperatorsParser } from './parsers/operators'
import DolfinRecord from '../record'
import { Enums, Tables } from '../db_types'
import { FunctionParser } from './parsers/function-parser'
import { DOLFIN_DSL_MATCHERS } from './parsers/utils/matchers'

export const DEALS_COLUMNS = [
  'amount',
  'close_date',
  'crm_provider',
  'flagged',
  'stage',
  'type',
  'currency',
  'name'
]
export const ACTIVITIES_COLUMNS = [
  'activity_date',
  'status',
  'type',
  ...DEALS_COLUMNS.map(column => `deal.${column}`)
]
export const INVOICES_COLUMNS = [
  'status',
  'currency',
  'due_date',
  'effective_at',
  'total',
  'total_excluding_tax',
  'amount_adjusted',
  'sub_total',
  'lines',
  'paid',
  'payment_date',
  'issued_credit_notes',
  'issued_credit_notes_without_taxes',
  'subscription_id',
  'subscription_start_date',
  'deal_invoice_order',
  ...DEALS_COLUMNS.map(column => `deal.${column}`)
]
export const LINE_ITEMS_COLUMNS = [
  'amount',
  'description',
  'unit_amount',
  'tax_amount',
  'tax_rate',
  'currency',
  'product_id',
  'product_name',
  'quantity',
  'discount_amount',
  ...DEALS_COLUMNS.map(column => `deal.${column}`),
  ...INVOICES_COLUMNS.map(column => `invoice.${column}`)
]
export const DEAL_LINE_ITEMS_COLUMNS = [
  'amount',
  'product_id',
  'product_name',
  'description',
  'sku',
  'quantity',
  'unit_amount',
  'currency',
  'discount_amount',
  'list_unit_amount',
  'pricebook_entry_id',
  'service_date',
  'billing_start_date',
  'billing_end_date',
  'billing_frequency',
  'billing_term',
  'crm_provider',
  ...DEALS_COLUMNS.map(column => `deal.${column}`)
]
export const KPIS_COLUMNS = ['period_start', 'profile_id']

export type CustomFieldsMap = Record<string, Tables<'custom_fields'>>
export type ParserVariableNamespace =
  | 'deal'
  | 'activity'
  | 'invoice'
  | 'line_item'
  | 'deal_line_item'
  | 'kpi'
  | 'user'
  | 'period'
export type FormulaParserContext = {
  customFields: CustomFieldsMap
  variableNamespaces: readonly ParserVariableNamespace[]
}

export const createFormulaParserContext = (
  customFields: CustomFieldsMap,
  variableNamespaces: readonly ParserVariableNamespace[] = []
): FormulaParserContext => ({
  customFields,
  variableNamespaces
})

interface ParseDolfinDSLArgs {
  recordData: DolfinRecord
  formula: string
  metaColumnsCache?: string[]
  customFields: CustomFieldsMap
  parserContext?: FormulaParserContext
  outcomePeriod?: Enums<'period'>
  inFormula?: boolean
}

class DolfinDSL {
  private parsers: DolfinParser[] = []

  recordData: DolfinRecord
  formula: string
  tags: string[] = []
  metaColumnsCache: string[] = []
  customFields: CustomFieldsMap
  parserContext: FormulaParserContext
  outcomePeriod?: Enums<'period'>
  inFormula: boolean

  static readonly META_REGEXP = DOLFIN_DSL_MATCHERS.META
  static readonly OPERATOR_REGEXP = DOLFIN_DSL_MATCHERS.OPERATOR
  static readonly FUNCTION_REGEXP = DOLFIN_DSL_MATCHERS.FUNCTION
  static readonly STRING_REGEXP = DOLFIN_DSL_MATCHERS.STRING
  static readonly NUMBER_REGEXP = DOLFIN_DSL_MATCHERS.NUMBER
  static readonly NULL_REGEXP = DOLFIN_DSL_MATCHERS.NULL

  constructor(args: ParseDolfinDSLArgs) {
    this.recordData = args.recordData
    this.metaColumnsCache = args.metaColumnsCache ?? []
    this.parserContext = args.parserContext ?? createFormulaParserContext(args.customFields)
    this.customFields = this.parserContext.customFields
    this.outcomePeriod = args.outcomePeriod
    this.inFormula = !!args.inFormula
    this.formula = args.formula.replace(/"/g, "'")

    this.initializeParsers()
  }

  private initializeParsers(): void {
    this.parsers = [
      new CustomFieldParser(
        this.recordData,
        this.metaColumnsCache,
        this.customFields,
        DolfinDSL.META_REGEXP
      ),
      new LegacyDbVariableParser(this.recordData),
      new RecordParser(this.recordData, this.getColumnsMatcher(), this.inFormula),
      new FunctionParser(this.recordData, DolfinDSL.FUNCTION_REGEXP),
      new OperatorsParser(this.recordData, DolfinDSL.OPERATOR_REGEXP),
      new IntToFloatParser(this.recordData)
    ]
  }

  private getColumnsMatcher(): RegExp {
    // Get columns from DB
    if (this.recordData.type === 'activity') {
      return new RegExp(`^(${ACTIVITIES_COLUMNS.join('|')})$`)
    }
    if (this.recordData.type === 'invoice') {
      return new RegExp(`^(${INVOICES_COLUMNS.join('|')})$`)
    }
    if (this.recordData.type === 'deal') {
      return new RegExp(`^(${DEALS_COLUMNS.join('|')})$`)
    }
    if (this.recordData.type === 'line_item') {
      return new RegExp(`^(${LINE_ITEMS_COLUMNS.join('|')})$`)
    }
    if (this.recordData.type === 'deal_line_item') {
      return new RegExp(`^(${DEAL_LINE_ITEMS_COLUMNS.join('|')})$`)
    }
    return new RegExp(`^(${KPIS_COLUMNS.join('|')})$`)
  }

  parse(): string {
    let formula = this.formula
    for (const parser of this.parsers) {
      formula = parser.execute(formula)
    }
    return formula
  }
}

export default DolfinDSL
